import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Territory } from "@/types/supabase";
import { useAuth } from "@/contexts/auth";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { getGroupSelectionState, getGroupedSelectionChips, mergeUniqueSelections, removeSelections } from "@/utils/areaSelection";

interface FilterBarProps extends React.HTMLAttributes<HTMLDivElement> {
  searchQuery: string;
  allCategories: string[];
  territories: Territory[];
  setActiveFilters: React.Dispatch<React.SetStateAction<{
    categories: string[];
    suburbs: string[];
    rating: string;
    verifiedOnly: boolean;
    showFavorites: boolean;
    topRatedOnly: boolean;
    featuredOnly: boolean;
  }>>;
  appliedFilters: {
    categories: string[];
    suburbs: string[];
    rating: string;
    verifiedOnly: boolean;
    showFavorites: boolean;
    topRatedOnly: boolean;
    featuredOnly: boolean;
  };
  setAppliedFilters: React.Dispatch<React.SetStateAction<{
    categories: string[];
    suburbs: string[];
    rating: string;
    verifiedOnly: boolean;
    showFavorites: boolean;
    topRatedOnly: boolean;
    featuredOnly: boolean;
  }>>;
  handleResetFilters: () => void;
}

const FilterBar = ({
  searchQuery,
  allCategories,
  territories,
  setActiveFilters,
  appliedFilters,
  setAppliedFilters,
  handleResetFilters,
  className,
  ...containerProps
}: FilterBarProps) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const territoryGroups = useMemo(
    () => Object.fromEntries(territories.map((territory) => [territory.name, territory.suburbs || []])),
    [territories]
  );

  const selectedAreaChips = useMemo(
    () => getGroupedSelectionChips(territoryGroups, appliedFilters.suburbs),
    [appliedFilters.suburbs, territoryGroups]
  );
  const areaFilterCount = selectedAreaChips.length;

  const primaryFilterButtonClassName = (isActive: boolean) => cn(
    "h-12 w-full justify-between rounded-md border border-white/20 bg-white px-4 text-sm font-medium text-brand-ink shadow-lg transition-colors hover:bg-brand-mist hover:text-brand-ink",
    isActive && "border-white bg-white text-brand-ink"
  );

  const secondaryFilterButtonClassName = (isActive: boolean) => cn(
    "h-9 rounded-md border px-3 text-sm font-medium shadow-md backdrop-blur transition-colors",
    isActive
      ? "border-white bg-white text-brand-ink hover:bg-brand-mist hover:text-brand-ink"
      : "border-white/25 bg-white/10 text-white hover:border-white/40 hover:bg-white/20 hover:text-white"
  );

  const updateSuburbFilters = (updater: (suburbs: string[]) => string[]) => {
    setActiveFilters((prev) => ({
      ...prev,
      suburbs: updater(prev.suburbs),
    }));

    setAppliedFilters((prev) => ({
      ...prev,
      suburbs: updater(prev.suburbs),
    }));
  };

  const handleCategoryToggle = (category: string) => {
    setActiveFilters(prev => {
      const categories = prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category];
      
      return { ...prev, categories };
    });
    
    setAppliedFilters(prev => {
      const categories = prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category];
      
      return { ...prev, categories };
    });
  };

  const handleSuburbToggle = (suburb: string) => {
    updateSuburbFilters((currentSuburbs) => (
      currentSuburbs.includes(suburb)
        ? currentSuburbs.filter((value) => value !== suburb)
        : [...currentSuburbs, suburb]
    ));
  };

  const handleTerritoryToggle = (territory: Territory) => {
    updateSuburbFilters((currentSuburbs) => {
      const selectionState = getGroupSelectionState(territory.suburbs, currentSuburbs);

      if (selectionState === "all") {
        return removeSelections(currentSuburbs, territory.suburbs);
      }

      return mergeUniqueSelections(currentSuburbs, territory.suburbs);
    });
  };

  const handleVerifiedToggle = (checked: boolean) => {
    setActiveFilters(prev => ({
      ...prev,
      verifiedOnly: checked
    }));
    
    setAppliedFilters(prev => ({
      ...prev,
      verifiedOnly: checked
    }));
  };

  const handleTopRatedToggle = (checked: boolean) => {
    setActiveFilters(prev => ({
      ...prev,
      topRatedOnly: checked
    }));
    
    setAppliedFilters(prev => ({
      ...prev,
      topRatedOnly: checked
    }));
  };

  const handleFeaturedToggle = (checked: boolean) => {
    setActiveFilters(prev => ({
      ...prev,
      featuredOnly: checked
    }));
    
    setAppliedFilters(prev => ({
      ...prev,
      featuredOnly: checked
    }));
  };

  const handleFavoritesToggle = (checked: boolean) => {
    if (checked && !user) {
      toast({
        title: "Login Required",
        description: "Please log in to filter by favorites",
        variant: "destructive"
      });
      return;
    }
    
    setActiveFilters(prev => ({
      ...prev,
      showFavorites: checked
    }));
    
    setAppliedFilters(prev => ({
      ...prev,
      showFavorites: checked
    }));
  };

  const hasActiveFilters = 
    appliedFilters.categories.length > 0 || 
    appliedFilters.suburbs.length > 0 || 
    appliedFilters.rating !== "" || 
    appliedFilters.verifiedOnly || 
    appliedFilters.showFavorites || 
    appliedFilters.topRatedOnly ||
    appliedFilters.featuredOnly ||
    searchQuery !== "";

  return (
    <div data-test-responsive="explore-filter-bar" {...containerProps} className={cn("mt-3 space-y-3", className)}>
      <div className="grid max-w-3xl grid-cols-1 gap-2 sm:grid-cols-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className={primaryFilterButtonClassName(appliedFilters.categories.length > 0)}
            >
              <span>Category</span>
              <span className="text-xs font-medium text-brand-slate">
                {appliedFilters.categories.length > 0 ? `${appliedFilters.categories.length} selected` : "All categories"}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[min(22rem,calc(100vw-2rem))] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search categories..." />
              <CommandList className="max-h-[300px]">
                <CommandEmpty>No matching categories found.</CommandEmpty>
                <CommandGroup heading="Categories">
                  {allCategories.map((category) => {
                    const isSelected = appliedFilters.categories.includes(category);

                    return (
                      <CommandItem
                        key={category}
                        value={category}
                        onSelect={() => handleCategoryToggle(category)}
                        className={cn(
                          "text-brand-slate",
                          isSelected && "bg-brand-mist font-medium text-brand-ink"
                        )}
                      >
                        <span>{category}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className={primaryFilterButtonClassName(appliedFilters.suburbs.length > 0)}
            >
              <span>Area</span>
              <span className="text-xs font-medium text-brand-slate">
                {areaFilterCount > 0 ? `${areaFilterCount} selected` : "All Hong Kong"}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[min(24rem,calc(100vw-2rem))] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search territories or areas..." />
              <CommandList>
                <CommandEmpty>No matching areas found.</CommandEmpty>
                {territories.map((territory) => {
                  const territoryState = getGroupSelectionState(territory.suburbs, appliedFilters.suburbs);

                  return (
                    <CommandGroup key={territory.name} heading={territory.name}>
                      <CommandItem
                        value={`all ${territory.name} ${territory.suburbs.join(" ")}`}
                        onSelect={() => handleTerritoryToggle(territory)}
                        className={cn(
                          "font-medium",
                          territoryState !== "none" && "bg-brand-mist text-brand-ink"
                        )}
                      >
                        <span>
                          {territoryState === "all" ? `Clear ${territory.name}` : `Select all in ${territory.name}`}
                        </span>
                      </CommandItem>
                      {territory.suburbs.map((suburb) => {
                        const isSelected = appliedFilters.suburbs.includes(suburb);

                        return (
                          <CommandItem
                            key={`${territory.name}-${suburb}`}
                            value={`${suburb} ${territory.name}`}
                            onSelect={() => handleSuburbToggle(suburb)}
                            className={cn(
                              "text-brand-slate",
                              isSelected && "bg-brand-mist font-medium text-brand-ink"
                            )}
                          >
                            <span>{suburb}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  );
                })}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex max-w-3xl flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          aria-pressed={appliedFilters.verifiedOnly}
          onClick={() => handleVerifiedToggle(!appliedFilters.verifiedOnly)}
          className={secondaryFilterButtonClassName(appliedFilters.verifiedOnly)}
        >
          Verified
        </Button>
        
        <Button
          type="button"
          variant="outline"
          aria-pressed={appliedFilters.topRatedOnly}
          onClick={() => handleTopRatedToggle(!appliedFilters.topRatedOnly)}
          className={secondaryFilterButtonClassName(appliedFilters.topRatedOnly)}
        >
          Top rated
        </Button>

        <Button
          type="button"
          variant="outline"
          aria-pressed={appliedFilters.featuredOnly}
          onClick={() => handleFeaturedToggle(!appliedFilters.featuredOnly)}
          className={secondaryFilterButtonClassName(appliedFilters.featuredOnly)}
        >
          Featured
        </Button>

        {user && (
          <Button
            type="button"
            variant="outline"
            aria-pressed={appliedFilters.showFavorites}
            onClick={() => handleFavoritesToggle(!appliedFilters.showFavorites)}
            className={secondaryFilterButtonClassName(appliedFilters.showFavorites)}
          >
            Favourites
          </Button>
        )}
        
        {hasActiveFilters && (
          <Button 
            type="button"
            variant="ghost" 
            size="sm" 
            onClick={handleResetFilters}
            className="h-9 rounded-md border border-white/15 bg-white/5 px-3 text-sm font-medium text-white shadow-md backdrop-blur hover:bg-white/15 hover:text-white sm:ml-auto"
          >
            Clear all
          </Button>
        )}
      </div>
    </div>
  );
};

export default FilterBar;
